/*
 * Search overlay (markup: snippets/search-overlay.liquid, styles: assets/component-search-overlay.css)
 * - Takes over the header's search icons (.js-search-form-open) instead of the theme's inline search bar
 * - Typing shows trending searches + matching products (Shopify predictive search)
 * - Mobile: the microphone fills the search from speech where the browser supports it
 * - Desktop: trending searches show as soon as it opens, no microphone
 */
(function () {
  var overlay = document.getElementById('SearchOverlay');
  if (!overlay) return;

  var form = overlay.querySelector('form');
  var input = overlay.querySelector('input[name="q"]');
  var micButton = overlay.querySelector('.js-search-overlay-mic');
  var productsSection = overlay.querySelector('[data-search-products]');
  var grid = overlay.querySelector('[data-search-grid]');
  var status = overlay.querySelector('[data-search-status]');
  var moreLink = overlay.querySelector('[data-search-more]');

  var mobileQuery = window.matchMedia('(max-width: 767.98px)');
  var MIN_CHARS = 2;
  var THROTTLE_MS = 120;
  var EAGER_IMAGES = 4; // first row(s) load straight away instead of waiting for lazy-loading
  var strings = (window.theme && window.theme.strings) || {};

  var lastTrigger = null;
  var lastTerm = '';
  var timeoutId = -1;
  var controller = null;
  var cache = {}; // search term -> products, so repeat searches and backspacing are instant

  function isMobile() {
    return mobileQuery.matches;
  }

  /* ---------- Open / close ---------- */

  // Sit the bar just under the header icons that opened it
  function positionBar(trigger) {
    var top = 12;
    var rect = trigger && trigger.getBoundingClientRect();
    if (rect && rect.height && rect.bottom > 0) top = rect.bottom + 12;
    overlay.style.setProperty('--search-overlay-top', Math.round(top) + 'px');
  }

  function updatePanel() {
    var hasTerm = input.value.trim().length > 0;
    // Mobile follows the design: the panel only appears once the shopper starts typing
    overlay.classList.toggle('has-panel', !isMobile() || hasTerm);
  }

  function open(trigger) {
    lastTrigger = trigger || document.activeElement;
    positionBar(trigger);
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('search-overlay-lock');
    document.body.classList.add('search-overlay-open');
    updatePanel();
    input.focus();
  }

  function close() {
    if (!overlay.classList.contains('is-open')) return;
    stopListening();
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('search-overlay-lock');
    document.body.classList.remove('search-overlay-open');
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus({ preventScroll: true });
      lastTrigger.blur();
    }
  }

  // Capture phase so the theme's own search-bar handlers (theme.js) never run for these triggers
  document.addEventListener('click', function (evt) {
    var trigger = evt.target.closest('.js-search-form-open');
    if (!trigger || overlay.contains(trigger)) return;
    evt.preventDefault();
    evt.stopPropagation();
    open(trigger);
  }, true);

  overlay.addEventListener('click', function (evt) {
    if (evt.target.closest('.js-search-overlay-close')) {
      evt.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (evt) {
    if (evt.key === 'Escape' && overlay.classList.contains('is-open')) close();
  });

  var onBreakpointChange = function () {
    if (!isMobile()) stopListening();
    updatePanel();
  };
  if (mobileQuery.addEventListener) {
    mobileQuery.addEventListener('change', onBreakpointChange);
  } else {
    mobileQuery.addListener(onBreakpointChange);
  }

  /* ---------- Matching products (mobile) ---------- */

  function formatMoney(amount) {
    if (window.theme && theme.Shopify && theme.Shopify.formatMoney) {
      return theme.Shopify.formatMoney(amount, theme.moneyFormatWithCodeForProductsPreference || theme.moneyFormat);
    }
    return amount;
  }

  // Product titles carry the style name in quotes, e.g. Flats "CAMILA - L42912"
  function displayTitle(title) {
    var match = title.match(/"([^"]+)"/);
    return match ? match[1] : title;
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + width;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  var heartSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.2-9.4C1.6 7.6 3.9 4 7.4 4c2 0 3.6 1.1 4.6 2.7C13 5.1 14.6 4 16.6 4c3.5 0 5.8 3.6 4.6 7.1-1.7 4.8-9.2 9.4-9.2 9.4Z"/></svg>';

  function buildCard(product, index) {
    var item = el('li', 'search-overlay__card');
    var link = el('a', 'search-overlay__card-link');
    link.href = product.url;

    var media = el('span', 'search-overlay__media');
    var imageUrl = (product.featured_image && product.featured_image.url) || product.image;
    if (imageUrl) {
      var img = el('img');
      img.src = sizedImage(imageUrl, 400);
      img.srcset = sizedImage(imageUrl, 300) + ' 300w, ' + sizedImage(imageUrl, 400) + ' 400w, ' + sizedImage(imageUrl, 600) + ' 600w';
      img.sizes = '(max-width: 767px) 40vw, 200px';
      img.alt = (product.featured_image && product.featured_image.alt) || product.title;
      if (index < EAGER_IMAGES) {
        img.fetchPriority = 'high';
      } else {
        img.loading = 'lazy';
      }
      media.appendChild(img);
    }
    link.appendChild(media);
    link.appendChild(el('span', 'search-overlay__title', displayTitle(product.title)));
    if (product.type) link.appendChild(el('span', 'search-overlay__type', product.type));

    var price = el('span', 'search-overlay__price');
    var priceMin = product.price_min || product.price;
    var current = el('span');
    current.innerHTML = formatMoney(priceMin);
    price.appendChild(current);
    if (parseFloat(product.compare_at_price_min) > parseFloat(priceMin)) {
      var compare = el('s');
      compare.innerHTML = formatMoney(product.compare_at_price_min);
      price.appendChild(compare);
    }
    link.appendChild(price);
    item.appendChild(link);

    // The wishlist app lives on the product page, so the heart takes shoppers there
    var heart = el('a', 'search-overlay__wishlist');
    heart.href = product.url;
    heart.setAttribute('aria-label', 'Add ' + displayTitle(product.title) + ' to wishlist');
    heart.tabIndex = -1;
    heart.innerHTML = heartSvg;
    item.appendChild(heart);

    return item;
  }

  function showStatus(message) {
    status.textContent = message || '';
  }

  function searchUrl(term) {
    var params = new URLSearchParams(new FormData(form));
    params.set('q', term);
    return form.getAttribute('action') + '?' + params.toString();
  }

  function renderResults(term, products) {
    productsSection.classList.remove('is-loading');
    grid.innerHTML = '';
    if (!products.length) {
      showStatus(strings.searchNoResults || 'No results found');
      moreLink.hidden = true;
      return;
    }
    showStatus('');
    var fragment = document.createDocumentFragment();
    products.forEach(function (product, index) {
      fragment.appendChild(buildCard(product, index));
    });
    grid.appendChild(fragment);
    moreLink.href = searchUrl(term);
    moreLink.textContent = 'View all results';
    moreLink.hidden = false;
  }

  function clearResults() {
    if (controller) controller.abort();
    clearTimeout(timeoutId);
    grid.innerHTML = '';
    showStatus('');
    moreLink.hidden = true;
    productsSection.hidden = true;
    productsSection.classList.remove('is-loading');
  }

  function fetchResults(term) {
    if (controller) controller.abort();
    controller = window.AbortController ? new AbortController() : null;

    var params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', overlay.dataset.searchMeta === 'true'
      ? 'title,product_type,variants.title,vendor,tag,variants.sku'
      : 'title,product_type,variants.title,vendor');

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'search/suggest.json?' + params.toString(), {
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(function (data) {
        var results = (data.resources && data.resources.results) || {};
        cache[term.toLowerCase()] = results.products || [];
        if (input.value.trim() !== term) return;
        renderResults(term, cache[term.toLowerCase()]);
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        productsSection.classList.remove('is-loading');
        grid.innerHTML = '';
        showStatus(strings.searchNoResults || 'No results found');
      });
  }

  function onInput() {
    updatePanel();
    var term = input.value.trim();
    if (term === lastTerm) return;
    lastTerm = term;

    if (term.length < MIN_CHARS) {
      clearResults();
      return;
    }

    productsSection.hidden = false;
    clearTimeout(timeoutId);

    var cached = cache[term.toLowerCase()];
    if (cached) {
      if (controller) controller.abort();
      renderResults(term, cached);
      return;
    }

    // Keep the previous results on screen (faded) until the new ones arrive
    productsSection.classList.add('is-loading');
    if (!grid.children.length) showStatus(strings.searchLoading || 'Loading...');
    timeoutId = setTimeout(fetchResults.bind(null, term), THROTTLE_MS);
  }

  input.addEventListener('input', onInput);

  form.addEventListener('submit', function (evt) {
    if (!input.value.trim()) {
      evt.preventDefault();
      input.focus();
    }
  });

  /* ---------- Voice search (mobile) ---------- */

  var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  var recognition = null;
  var listening = false;
  var placeholder = input.getAttribute('placeholder');

  function setListening(state) {
    listening = state;
    micButton.classList.toggle('is-listening', state);
    micButton.setAttribute('aria-pressed', state ? 'true' : 'false');
    input.setAttribute('placeholder', state ? 'Listening…' : placeholder);
  }

  function stopListening() {
    if (recognition && listening) recognition.abort();
    if (listening) setListening(false);
  }

  function startListening() {
    if (!recognition) {
      recognition = new SpeechRecognition();
      recognition.lang = document.documentElement.lang === 'en' ? 'en-US' : (document.documentElement.lang || 'en-US');
      recognition.interimResults = true;
      recognition.continuous = false;
      recognition.maxAlternatives = 1;

      recognition.onresult = function (evt) {
        var transcript = '';
        var isFinal = false;
        for (var i = 0; i < evt.results.length; i++) {
          transcript += evt.results[i][0].transcript;
          if (evt.results[i].isFinal) isFinal = true;
        }
        input.value = transcript.replace(/[.?!]+$/, '');
        onInput();
        if (isFinal) stopListening();
      };

      recognition.onerror = function (evt) {
        setListening(false);
        if (evt.error === 'not-allowed' || evt.error === 'service-not-allowed') {
          input.setAttribute('placeholder', 'Allow microphone access to search by voice');
          setTimeout(function () { input.setAttribute('placeholder', placeholder); }, 3000);
        }
      };

      recognition.onend = function () {
        setListening(false);
      };
    }

    try {
      recognition.start();
      setListening(true);
    } catch (e) {
      // start() throws if a previous session is still closing; the next tap will work
      setListening(false);
    }
  }

  // Voice search needs the Web Speech API and a secure page; otherwise the mic stays hidden
  if (SpeechRecognition && window.isSecureContext !== false) {
    micButton.hidden = false;
    micButton.addEventListener('click', function () {
      if (listening) {
        recognition.stop();
      } else {
        input.blur(); // drop the keyboard so the shopper can see the results while speaking
        startListening();
      }
    });
  }
})();
